import { Fragment, useEffect, useState } from "react";
import { Loader2, ShieldCheck, Globe, Building2, Pencil, Trash2, Plus, X, Save } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Roles() {
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("roles.create");
  const canEdit = hasPermission("roles.edit");
  const canDelete = hasPermission("roles.delete");
  const canViewPerms = hasPermission("permissions.view");

  const [roles, setRoles] = useState([]);
  const [permModules, setPermModules] = useState({});
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // role being edited/created

  const load = async () => {
    try {
      const reqs = [api.get("/roles")];
      if (canViewPerms) reqs.push(api.get("/permissions"));
      const res = await Promise.all(reqs);
      setRoles(res[0].data);
      if (res[1]) setPermModules(res[1].data.modules);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (r) => {
    if (!window.confirm(`Delete role "${r.name}"?`)) return;
    try {
      await api.delete(`/roles/${r.id}`);
      toast.success("Role deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (loading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div>;

  const allPerms = Object.values(permModules).flat();

  return (
    <div className="space-y-6" data-testid="roles-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Access Management</p>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-ink-text">Roles &amp; Permissions</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Database-driven RBAC. Each role carries a scope and a set of permission keys.</p>
        </div>
        {canCreate && (
          <button onClick={() => setEditor({ mode: "create", name: "", key: "", description: "", permission_keys: [], scope_type: "company", system_default: false })} data-testid="add-role-button" className="flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-500 dark:bg-ink-gold dark:bg-ink-gold dark:text-[#0B1120] dark:hover:bg-ink-golddeep">
            <Plus className="h-4 w-4" /> New role
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="roles-grid">
        {roles.map((r) => {
          const isPlatform = r.scope_type === "platform";
          const wildcard = r.permission_keys.includes("*");
          return (
            <div key={r.id} data-testid={`role-card-${r.key}`} className="flex flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-ink-line dark:bg-ink-card">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2.5">
                  <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${isPlatform ? "bg-indigo-50 text-indigo-600 dark:bg-ink-gold/10 dark:text-ink-gold" : "bg-sky-50 text-sky-600 dark:bg-ink-gold/10 dark:text-ink-gold"}`}>
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-display font-semibold text-slate-900 dark:text-ink-text">{r.name}</div>
                    <div className="font-mono-tf text-[11px] text-slate-400">{r.key}</div>
                  </div>
                </div>
                <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${isPlatform ? "bg-indigo-50 text-indigo-600 dark:bg-ink-gold/10 dark:text-ink-gold" : "bg-slate-100 text-slate-500 dark:bg-ink-panel dark:text-slate-400"}`}>
                  {isPlatform ? <Globe className="h-3 w-3" /> : <Building2 className="h-3 w-3" />} {r.scope_type}
                </span>
              </div>
              <p className="mt-3 flex-1 text-sm text-slate-500 dark:text-slate-400">{r.description}</p>
              <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3 dark:border-ink-line">
                <span className="text-xs font-medium text-slate-500">
                  {wildcard ? "All permissions" : `${r.permission_keys.length} permissions`}
                </span>
                <div className="flex gap-1">
                  {r.system_default && <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-600 dark:bg-amber-500/15 dark:text-amber-300">system</span>}
                  {canEdit && !isPlatform && (
                    <button onClick={() => setEditor({ mode: "edit", ...r })} data-testid={`edit-role-${r.key}`} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-sky-600 dark:hover:bg-ink-panel"><Pencil className="h-4 w-4" /></button>
                  )}
                  {canDelete && !r.system_default && (
                    <button onClick={() => remove(r)} data-testid={`delete-role-${r.key}`} className="rounded-md p-1.5 text-slate-500 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-500/10"><Trash2 className="h-4 w-4" /></button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {canViewPerms && allPerms.length > 0 && (
        <PermissionMatrix roles={roles} permModules={permModules} />
      )}

      {editor && (
        <RoleEditor editor={editor} permModules={permModules} onClose={() => setEditor(null)} onSaved={() => { setEditor(null); load(); }} />
      )}
    </div>
  );
}

function PermissionMatrix({ roles, permModules }) {
  const companyRoles = roles.filter((r) => !r.permission_keys.includes("*"));
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-ink-line dark:bg-ink-card" data-testid="permission-matrix">
      <div className="border-b border-slate-100 px-5 py-4 dark:border-ink-line">
        <h2 className="font-display font-semibold text-slate-900 dark:text-ink-text">Permission Matrix</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">Permission keys granted per role (platform Super Admin has all).</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 dark:border-ink-line dark:bg-ink/40">
              <th className="sticky left-0 bg-slate-50 px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500 dark:bg-ink/40">Permission</th>
              {companyRoles.map((r) => (
                <th key={r.id} className="px-3 py-2 text-center text-[11px] font-medium text-slate-500">{r.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(permModules).map(([mod, perms]) => (
              <Fragment key={mod}>
                <tr className="bg-slate-50/60 dark:bg-ink-panel/30">
                  <td colSpan={companyRoles.length + 1} className="px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">{mod}</td>
                </tr>
                {perms.map((p) => (
                  <tr key={p.key} className="border-b border-slate-100 last:border-0 dark:border-ink-line" data-testid={`matrix-row-${p.key}`}>
                    <td className="sticky left-0 bg-white px-4 py-1.5 font-mono-tf text-[11px] text-slate-600 dark:bg-ink-card dark:text-slate-300">{p.key}</td>
                    {companyRoles.map((r) => (
                      <td key={r.id} className="px-3 py-1.5 text-center">
                        {r.permission_keys.includes(p.key) ? (
                          <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
                        ) : (
                          <span className="inline-block h-2 w-2 rounded-full bg-slate-200 dark:bg-ink-panel" />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RoleEditor({ editor, permModules, onClose, onSaved }) {
  const isCreate = editor.mode === "create";
  const [form, setForm] = useState({ name: editor.name, key: editor.key, description: editor.description, permission_keys: editor.permission_keys || [] });
  const [saving, setSaving] = useState(false);

  const toggle = (key) => setForm((f) => ({ ...f, permission_keys: f.permission_keys.includes(key) ? f.permission_keys.filter((k) => k !== key) : [...f.permission_keys, key] }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isCreate) {
        await api.post("/roles", { name: form.name, key: form.key, description: form.description, permission_keys: form.permission_keys });
        toast.success("Role created");
      } else {
        await api.patch(`/roles/${editor.id}`, { name: form.name, description: form.description, permission_keys: form.permission_keys });
        toast.success("Role updated");
      }
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="role-editor">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl rounded-xl border border-slate-200 bg-white shadow-2xl dark:border-ink-line dark:bg-ink-card">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 dark:border-ink-line">
          <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-ink-text">{isCreate ? "New Role" : `Edit ${editor.name}`}</h3>
          <button onClick={onClose} data-testid="role-editor-close" className="rounded-md p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-ink-panel"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="max-h-[72vh] space-y-4 overflow-y-auto px-6 py-5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">Name</label>
              <input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="role-name-input" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-ink-line dark:bg-ink-panel dark:text-ink-text" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">Key</label>
              <input required disabled={!isCreate} value={form.key} onChange={(e) => setForm((f) => ({ ...f, key: e.target.value }))} data-testid="role-key-input" className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono-tf text-sm disabled:bg-slate-50 disabled:text-slate-500 dark:border-ink-line dark:bg-ink-panel dark:text-ink-text" />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">Description</label>
            <input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="role-description-input" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-ink-line dark:bg-ink-panel dark:text-ink-text" />
          </div>
          <div>
            <label className="mb-2 block text-xs font-medium uppercase tracking-wider text-slate-400">Permissions</label>
            <div className="space-y-3">
              {Object.entries(permModules).map(([mod, perms]) => (
                <div key={mod} className="rounded-lg border border-slate-100 p-3 dark:border-ink-line">
                  <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">{mod}</div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {perms.map((p) => (
                      <label key={p.key} className="flex cursor-pointer items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                        <input type="checkbox" checked={form.permission_keys.includes(p.key)} onChange={() => toggle(p.key)} data-testid={`role-perm-${p.key}`} className="accent-sky-600 dark:accent-ink-gold" />
                        <span className="font-mono-tf">{p.key}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 dark:border-ink-line dark:text-slate-300">Cancel</button>
            <button type="submit" disabled={saving} data-testid="role-editor-save" className="flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-500 dark:bg-ink-gold dark:bg-ink-gold dark:text-[#0B1120] dark:hover:bg-ink-golddeep disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {isCreate ? "Create role" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
